import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import QuoteSection from '../components/QuoteSection';
import QuickQuoteForm from '../components/QuickQuoteForm';

const SnowRemovalCromwellPage = () => {
  const packages = [
    {
      id: 1,
      name: 'Cromwell Basic',
      price: 'Starting at $500/season',
      description: 'Perfect for Cromwell residential properties',
      features: [
        'Driveway snow plowing (up to 2 cars)',
        'Walkway clearing to front door',
        'Front entrance steps cleared',
        'Priority service during storms',
        '24/7 emergency response in Cromwell'
      ]
    },
    {
      id: 2,
      name: 'Cromwell Premium',
      price: 'Starting at $1000/season',
      description: 'Complete snow removal for Cromwell homes',
      features: [
        'Full driveway and parking area',
        'All walkways and pathways cleared',
        'Front and back entrances',
        'Salt/sand application',
        'Ice removal services',
        'Priority scheduling for Cromwell residents',
        '24/7 emergency response'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Cromwell Commercial',
      price: 'Custom Pricing',
      description: 'Professional snow removal for Cromwell businesses',
      features: [
        'Business parking lot clearing',
        'Sidewalk maintenance',
        'Entrance areas kept safe',
        'Salt/sand application',
        'Early morning service before business hours',
        '24/7 emergency response',
        'Liability insurance included'
      ]
    }
  ];

  const cromwellAreas = [
    'Cromwell Center',
    'North Cromwell',
    'South Cromwell',
    'West Side',
    'East Side',
    'Route 9 Corridor',
    'Main Street District',
    'Riverfront Area'
  ];

  const overviewHighlights = [
    'River-hardened crews pretreat Main Street, Nooks Hill, and Skyview Estates before Route 9 traffic starts.',
    'Sidewalk teams scrape storefronts, townhome steps, and municipal facilities while plow trucks finish the lots.',
    'Bridge and bluff monitoring lets us return with calcium treatments before overnight refreeze.'
  ];

  const serviceStats = [
    { value: '4:15 AM', label: 'First dispatch' },
    { value: '12', label: 'Dedicated loops' },
    { value: '45 min', label: 'Average return' }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Snow Removal Service Cromwell CT - G&D Landscaping",
    "description": "Professional snow removal services in Cromwell, Connecticut. Reliable snow plowing, ice management, and winter maintenance for homes and businesses.",
    "logo": "https://www.gdlandscapingllc.com/GD.png",
    "image": [
      "https://www.gdlandscapingllc.com/GD_Landscaping_LLC_Snow_removal.JPG",
      "https://www.gdlandscapingllc.com/Gabe_Eltman_GD_Landscaping_LLC.JPG",
      "https://www.gdlandscapingllc.com/snow-plow-clearing-driveway-connecticut.webp"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Cromwell",
      "addressRegion": "CT",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 41.6126,
      "longitude": -72.6581
    },
    "telephone": "(860) 526-7583",
    "url": "https://www.gdlandscapingllc.com/snow-removal-cromwell-ct",
    "serviceArea": {
      "@type": "City",
      "name": "Cromwell, Connecticut"
    }
  };

  return (
    <>
      <SEOHead
        title="Snow Removal Cromwell CT | G&D Landscaping"
        description="Reliable snow removal services in Cromwell, Connecticut. Seasonal packages for homes and businesses. Fast response, professional equipment, fully insured. Call (860) 526-7583."
        keywords="snow removal Cromwell CT, snow plowing Cromwell Connecticut, winter services Cromwell, ice management Cromwell CT, residential snow removal Cromwell, commercial snow plowing Cromwell"
        canonicalUrl="https://www.gdlandscapingllc.com/snow-removal-cromwell-ct"
        structuredData={structuredData}
      />

      <div className="snow-removal-section">
        {/* Cromwell Hero Section */}
        <div className="snow-hero" style={{
          backgroundImage: 'linear-gradient(rgba(30, 58, 138, 0.7), rgba(59, 130, 246, 0.7)), url(/GD_Landscaping_LLC_Snow_removal.JPG)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}>
          {/* SEO Image */}
          <img
            src="/GD_Landscaping_LLC_Snow_removal.JPG"
            alt="G&D Landscaping professional snow removal service in Connecticut"
            style={{display: 'none'}}
            width="1920"
            height="1080"
          />
          <div className="snow-hero-overlay"></div>
          <div className="snow-hero-content">
            <div className="container">
              <div className="hero-badge">
                <span>❄️ Serving Cromwell, CT</span>
              </div>
              <h1>Snow Removal Service Cromwell CT</h1>
              <p className="hero-subtitle">Riverfront squalls, steep drives, and city sidewalks get cleared on precise routes so Cromwell homeowners and storefronts stay open during every storm.</p>
              <QuickQuoteForm source="snow-removal-cromwell" />
              <div className="hero-buttons">
                <Link to="/contact" className="cta-primary">
                  Get Cromwell Quote
                </Link>
                <a href="tel:(860) 526-7583" className="cta-secondary">
                  Call (860) 526-7583
                </a>
              </div>
            </div>
          </div>
        </div>

        <section className="town-overview">
          <div className="container">
            <div className="town-overview-grid">
              <div className="town-overview-card">
                <h2>Cromwell Winter Strategy</h2>
                <p>Connecticut River moisture creates heavy, fast accumulations. We stage equipment around town to pre-salt, plow, and return before ice rebuilds.</p>
                <ul>
                  {overviewHighlights.map((item, idx) => (
                    <li key={`cromwell-snow-highlight-${idx}`}>
                      <span>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="town-overview-card">
                <h3>Cromwell Service Snapshot</h3>
                <p>Communication stays tight with text alerts and crew tracking during every storm.</p>
                <div className="town-stats">
                  {serviceStats.map((stat) => (
                    <div key={stat.label} className="town-stat">
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container">
          {/* Cromwell Specific Benefits */}
          <section className="location-benefits">
            <div className="section-header">
              <h2>Why Cromwell Chooses G&D Landscaping</h2>
              <p className="section-subtitle">We understand Cromwell's unique geography and winter weather patterns</p>
            </div>

            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🌊</div>
                <h3>River Town Expertise</h3>
                <p>Experience with Cromwell's unique weather patterns influenced by the Connecticut River and varied terrain.</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">⚡</div>
                <h3>Quick Response</h3>
                <p>Located just minutes away in Berlin, CT - fast response times throughout all Cromwell neighborhoods.</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🏘️</div>
                <h3>Residential Focus</h3>
                <p>Specialized service for Cromwell's mix of suburban homes, condos, and waterfront properties.</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">💼</div>
                <h3>Commercial Ready</h3>
                <p>Professional snow removal for Cromwell's business district and retail centers along Route 9.</p>
              </div>
            </div>
          </section>

          {/* Cromwell Packages */}
          <section className="packages-section">
            <div className="section-header">
              <h2>Cromwell Snow Removal Packages</h2>
              <p className="section-subtitle">Tailored winter maintenance solutions for Cromwell properties</p>
            </div>

            <div className="packages-grid">
              {packages.map((pkg) => (
                <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`}>
                  {pkg.popular && <div className="popular-badge">Most Popular in Cromwell</div>}
                  <div className="package-header">
                    <h3>{pkg.name}</h3>
                    <div className="package-price">{pkg.price}</div>
                    <p className="package-description">{pkg.description}</p>
                  </div>
                  <div className="package-features">
                    <ul>
                      {pkg.features.map((feature, index) => (
                        <li key={index}>
                          <span className="feature-check">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="package-footer">
                    <Link to="/contact" className="package-btn">
                      Get Cromwell Quote
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Cromwell Areas Served */}
          <section className="location-areas">
            <div className="section-header">
              <h2>Cromwell Areas We Serve</h2>
              <p className="section-subtitle">Complete snow removal coverage throughout Cromwell, Connecticut</p>
            </div>

            <div className="areas-content">
              <div className="local-areas">
                <h3>Cromwell Neighborhoods & Areas</h3>
                <div className="towns-grid">
                  {cromwellAreas.map((area, index) => (
                    <span key={index} className="town">{area}</span>
                  ))}
                </div>
              </div>

              <div className="service-highlights">
                <div className="highlight-item">
                  <h4>🏢 Business District</h4>
                  <p>Professional snow removal for Cromwell's Main Street businesses and Route 9 commercial areas.</p>
                </div>
                <div className="highlight-item">
                  <h4>🏘️ Residential Communities</h4>
                  <p>Reliable snow removal for all Cromwell neighborhoods, from historic center to newer developments.</p>
                </div>
                <div className="highlight-item">
                  <h4>🌊 Waterfront Properties</h4>
                  <p>Specialized service for Connecticut River waterfront homes and marinas.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Local Knowledge Section */}
          <section className="local-knowledge">
            <div className="section-header">
              <h2>Local Cromwell Winter Expertise</h2>
            </div>

            <div className="knowledge-content">
              <div className="knowledge-text">
                <h3>We Know Cromwell's Winter Conditions</h3>
                <p>Cromwell's location along the Connecticut River creates unique winter weather challenges that we're equipped to handle:</p>
                <ul>
                  <li><strong>River Effect Weather:</strong> Understanding how the Connecticut River influences local snow patterns</li>
                  <li><strong>Varied Terrain:</strong> Experience with Cromwell's hills, flats, and waterfront elevation changes</li>
                  <li><strong>Route 9 Access:</strong> Ensuring businesses stay accessible during heavy snow events</li>
                  <li><strong>Residential Diversity:</strong> Service for everything from historic homes to modern subdivisions</li>
                </ul>
              </div>
            </div>
          </section>
            <QuoteSection
            title="Reserve Cromwell Snow Service"
            subtitle="Tell us about your driveway, parking lot, or facility access and we’ll send a tailored snow plan."
            locationName="Cromwell Snow"
            source="Cromwell Snow Page"
          />

          {/* Contact CTA */}
          <section className="snow-cta">
            <div className="cta-content">
              <h2>Ready for Winter in Cromwell?</h2>
              <p>Join your Cromwell neighbors who trust G&D Landscaping for reliable snow removal. Secure your seasonal package today!</p>
              <div className="cta-buttons">
                <Link to="/contact" className="cta-primary">
                  Get Your Cromwell Quote
                </Link>
                <a href="tel:(860) 526-7583" className="cta-secondary">
                  Call (860) 526-7583
                </a>
              </div>
              <p className="service-note">Serving Cromwell, CT and surrounding areas • Licensed & Insured • 24/7 Emergency Response</p>
            </div>
          </section>
        </div>

      </div>
    </>
  );
};

export default SnowRemovalCromwellPage;
